import React, { useRef, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';
import { API_BASE_URL } from '../../utils/apiConfig';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;
  font-family: 'serif';
`;

const Page = styled.div`
  page-break-after: always;
  margin-bottom: 60px;
  padding: 32px 24px 40px 24px;
`;

const Logo = styled.img`
  display: block;
  margin: 0 0 16px auto;
  max-width: 120px;
  float: right;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;
  
  &:hover:not(:disabled) {
    background: #2c3e50;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const MedicalEvaluationForm = () => {
  const docRef = useRef();
  const { data: formData, loading, error } = useEnrollmentFormData();
  const [medicalEvalData, setMedicalEvalData] = useState(null);
  const [medicalEvalLoading, setMedicalEvalLoading] = useState(false);
  const [medicalEvalError, setMedicalEvalError] = useState(null);

  const handleDownload = () => {
    window.print();
  };

  // Fetch medical evaluation form data
  useEffect(() => {
    const fetchMedicalEvalData = async () => {
      if (!formData?.id) return;

      setMedicalEvalLoading(true);
      setMedicalEvalError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/students/medical-evaluation/${formData.id}`);
        const result = await response.json();

        if (result.success) {
          setMedicalEvalData(result.data);
        } else {
          setMedicalEvalError(result.message || 'Failed to load medical evaluation data');
        }
      } catch (err) {
        console.error('Error fetching medical evaluation data:', err);
        setMedicalEvalError('Error loading medical evaluation data');
      } finally {
        setMedicalEvalLoading(false);
      }
    };

    fetchMedicalEvalData();
  }, [formData?.id]);

  if (loading || medicalEvalLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading student information...
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
          Error loading student information: {error}
        </div>
      </Container>
    );
  }

  if (!formData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          No student data found.
        </div>
      </Container>
    );
  }

  // Extract student data from formData
  const student = formData;

  return (
    <Container style={{ fontFamily: 'sans-serif' }}>
      <PrintHideStyle />
      <div ref={docRef}>
        {/* Page 1 */}
        <Page>
          <div style={{ display: 'flex' }}>
            <Logo src={process.env.PUBLIC_URL + '/form_logo_2.png'} alt="CCLC Logo" />
          </div>
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '14px', marginBottom: 4, marginTop: 8, fontFamily: 'sans-serif' }}>
            MEDICAL EVALUATION / PHYSICAL EXAMINATION<br />
            FORM- PART 1 OF 2
          </div>
          <div style={{ fontWeight: 700, marginBottom: 8, marginTop: 8, fontSize: '14px' }}>PART I: To be completed by student</div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0, display: 'inline-table' }} border="1">
            <thead>
              <tr>
                <td colSpan={4} style={{ padding: '4px 0', marginBottom: 8, fontSize: '12px', textAlign: 'center', fontWeight: 700, border: 'none' }}>CHECK THE BOX FOR THE APPROPRIATE NURSING/ ALLIED HEALTH PROGRAM</td>
              </tr>
              <tr>
                <td colSpan={4} ><input type="checkbox" style={{ marginRight: 50, border: 'none' }} checked={student.program_nursing_assistant} readOnly />Nursing Assistant
                </td>
              </tr>



              <tr>
                <td colSpan={4} style={{ padding: '4px 0', marginBottom: 8, fontSize: '12px', textAlign: 'center', fontWeight: 700, border: 'none' }}>STUDENT INFORMATION</td>
              </tr>
            </thead>
            <tbody>
              <tr style={{ fontSize: '12px' }}>
                <td>Last Name: {student.lastName || ''}</td>
                <td>First Name: {student.firstName || ''}</td>
                <td>MI: {student.bc_full_middle_name || ''}</td>
                <td>Maiden: {student.bc_other_names || ''}</td>
              </tr>
              <tr style={{ fontSize: '12px' }}>
                <td >Address: {student.enroll_address || ''}</td>
                <td>City: {student.enroll_city || ''}</td>
                <td>State: {student.enroll_state || ''}</td>
                <td>Zip Code: {student.enroll_zip || ''}</td>
              </tr>
              <tr style={{ fontSize: '12px' }}>
                <td>Email: {student.email || ''}</td>
                <td>Birth date: {student.bc_dob ? new Date(student.bc_dob).toLocaleDateString('en-US') : ''}</td>
                <td colSpan={2}>Gender: {student.med_gender === 'Male' ? '✓ Male' : '□ Male'} {student.med_gender === 'Female' ? '✓ Female' : '□ Female'}</td>
              </tr>
              <tr style={{ fontSize: '12px' }}>
                <td colSpan={2}>Home Phone: {student.enroll_phone_h || '_____'}</td>
                <td>Work Phone: {student.enroll_phone_w || '_____'}</td>
                <td colSpan={2}>Cell Phone: {student.enroll_phone_c || '_____'}</td>
              </tr>
            </tbody>
          </table>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0, display: 'inline-table', borderTop: 'none' }} border="1">
            <thead>
              <tr>
                <td colSpan={4} style={{ padding: '4px 0', marginBottom: 8, fontSize: '12px', textAlign: 'center', fontWeight: 700 }}>PERSON TO NOTIFY IN CASE OF EMERGENCY</td>
              </tr>
            </thead>
            <tbody>
              <tr style={{ fontSize: '12px' }}>
                <td colSpan={2}>Full Name: {student.em_full_name || ''}</td>
                <td colSpan={2}>Relationship: {student.em_relationship || ''}</td>
              </tr>
              <tr style={{ fontSize: '12px' }}>
                <td>Address: {student.em_address || ''}</td>
                <td>City: {student.em_city || ''}</td>
                <td>State: {student.state_select || ''}</td>
                <td>Zip Code: {student.em_zip || ''}</td>
              </tr>
              <tr style={{ fontSize: '12px' }}>
                <td>Phone (Home): {student.em_phone_home || ''}</td>
                <td>Phone (Work): {student.em_phone_work || ''}</td>
                <td>Phone (Cell): {student.em_phone_cell || ''}</td>
              </tr>
            </tbody>
          </table>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0, display: 'inline-table', borderTop: 'none' }} border="1">
            <thead>
              <tr>
                <td colSpan={4} style={{ padding: '4px 0', marginBottom: 8, fontSize: '12px', textAlign: 'center', fontWeight: 700 }}>HEALTH HISTORY</td>
              </tr>
            </thead>
            <tbody>
              <tr style={{ fontSize: '12px' }}><td>Rate your current health status:</td><td>{student.health_status === 'Excellent' ? '✓ Excellent' : '□ Excellent'} {student.health_status === 'Good' ? '✓ Good' : '□ Good'} {student.health_status === 'Fair' ? '✓ Fair' : '□ Fair'} {student.health_status === 'Poor' ? '✓ Poor' : '□ Poor'}</td></tr>
              <tr style={{ fontSize: '12px' }}><td>Allergies:</td><td>{student.allergies || ''}</td></tr>
              <tr style={{ fontSize: '12px' }}><td>Medications?: {student.medications_present === 'Yes' ? '✓ Yes' : '□ Yes'} {student.medications_present === 'No' ? '✓ No' : '□ No'}-List medication and reaction</td><td>{student.meds_list || ''}</td></tr>
              <tr style={{ fontSize: '12px' }}><td>Latex allergy?: {student.latex_allergy === 'Yes' ? '✓ Yes' : '□ Yes'} {student.latex_allergy === 'No' ? '✓ No' : '□ No'}</td><td>Food allergy?: {student.food_allergy === 'Yes' ? '✓ Yes' : '□ Yes'} {student.food_allergy === 'No' ? '✓ No' : '□ No'} - List food(s)/reaction {student.food_list || ''}</td></tr>
              <tr style={{ fontSize: '12px' }}><td colSpan={2}>Are you pregnant? {student.pregnant === 'Yes' ? '✓ Yes' : '□ Yes'}-Due Date? Are you seeing an OB/GYN? {student.obgyn === 'Yes' ? '✓ Yes' : '□ Yes'} {student.obgyn === 'No' ? '✓ No' : '□ No'}</td></tr>
              <tr style={{ fontSize: '12px' }}><td colSpan={2}>Do you have any lifting, pushing, pulling, bending, or twisting restrictions: {student.lifting_restrictions === 'Yes' ? '✓ Yes' : '□ Yes'} {student.lifting_restrictions === 'No' ? '✓ No' : '□ No'} – Please explain:<br /><div style={{ height: 24 }}>{student.restrictions_explain || ''}</div></td></tr>
            </tbody>
          </table>
          {/* add it here */}
          <div style={{ border: '1px solid black', borderTop: 'none', padding: '0 10px 10px 10px' }}>
            <div style={{ fontWeight: 700, padding: '4px 0', paddingTop: 12, marginBottom: 8, fontSize: '12px', textAlign: 'center' }}>ESSENTIAL TECHNICAL STANDARDS</div>
            <div style={{ fontSize: '12px', marginBottom: 16, lineHeight: '1.4' }}>
              The attendance requirements and stamina demand require the Nursing Assistant and Allied Health students to be in good physical and mental health. In the interest of the student's personal safety and the safety of patients and clients. and other healthcare workers, students must meet, with or without reasonable accommodation. the Essential Technical Standards before admission into the program is finalized. Students are required to complete a self-assessment of their capability to meet these standards. Please read the Essential Technical Standards carefully, and initial each technical standard, only if you can comply with the standard. Students must present a completed copy of the Essential Technical Standards to their Healthcare Provider for review at the time of their physical examination. When complete, please sign, date and upload it into your Electronic Profile. If a student cannot meet one or more of the standards, they must meet with the Director of the Program for which they have applied to discuss reasonable accommodations.</div>

            <div style={{ marginBottom: 16 }}>
              <strong style={{ fontSize: '12px' }}>Do you have any conditions that would interfere with your ability to perform the Essential Technical Standards?</strong>
              <div style={{ marginTop: 8 }}>
                <span style={{ marginRight: '20px', fontSize: '12px' }}>
                  <input type="checkbox" checked={student.technical_standards_conditions === 'No'} readOnly /> No
                </span>
                <span style={{ fontSize: '12px' }}>
                  <input type="checkbox" checked={student.technical_standards_conditions === 'Yes'} readOnly /> Yes
                </span>
                {student.technical_standards_conditions === 'Yes' && (
                  <span style={{ marginLeft: '10px' }}>If yes, please explain: <span style={{ borderBottom: '1px solid #000', padding: '0 8px', minWidth: '300px', display: 'inline-block' }}>{student.technical_standards_explanation || ''}</span></span>
                )}
              </div>
            </div>

            <div style={{ fontSize: '12px', marginBottom: 16, lineHeight: '1.4', fontWeight: 700 }}>
              The information provided is true and correct to the best of my knowledge. I am aware that ANY change in my physical or mental health status, including pregnancy and/or medication use must be immediately reported to the Director of the Nursing or Allied Health Program to which I am assigned. Chicago Community Learning Center has my permission to release these test results to the clinical education agencies to which I am assigned. My Healthcare Provider may release health related information to the Director of the Program in which I am enrolled.
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <div>
                <strong style={{ fontSize: '12px' }}>STUDENT SIGNATURE:</strong>
                <div style={{ borderBottom: '1px solid #000', padding: '0 8px', minWidth: '250px', display: 'inline-block', fontSize: '12px', marginLeft: '10px' }}>
                  {student.student_ack_signature_data ? (
                    <img
                      src={student.student_ack_signature_data}
                      alt="Student Signature"
                      style={{
                        maxHeight: '40px',
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No signature available</span>
                  )}
                </div>
              </div>
              <div style={{ alignContent: 'end' }}>
                <strong style={{ fontSize: '12px' }}>DATE:</strong>
                <div style={{ borderBottom: '1px solid #000', padding: '0 8px', minWidth: '150px', display: 'inline-block', fontSize: '12px', marginLeft: '10px' }}>
                  {student.acknowledgementDate || ''}
                </div>
              </div>
            </div></div>

        </Page>
        {/* Page 2 */}
        <Page>
          <div>
            <Logo style={{ display: 'flex', marginBottom: 0 }} src={process.env.PUBLIC_URL + '/form_logo_2.png'} alt="CCLC Logo" />
          </div>

          <div style={{ fontSize: '0.85rem', lineHeight: '1.3', }}>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, display: 'inline-table', fontSize: '0.8rem' }} border="1">

            <thead>
              <tr>
                <td colSpan={4} style={{ padding: '4px 0', marginBottom: 8, fontSize: '12px', textAlign: 'center' }}>ESSENTIAL TECHNICAL STANDARDS</td>

              </tr>
              <tr>
                <td colSpan={4} style={{ padding: 5 }}>PART I (CON'T): To be completed by student. Initial ONLY if you can comply with the standard.
                </td>
              </tr>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ width: '20%', padding: '8px 4px' }}>Requirements</th>
                <th style={{ width: '35%', padding: '8px 4px' }}>Standards</th>
                <th style={{ width: '35%', padding: '8px 4px' }}>Examples</th>
                <th style={{ width: '10%', padding: '8px 4px' }}>Student's Initials</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Mobility</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Physical ability, flexibility, strength, and stamina</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Various abilities for long periods of time, including standing, walking, bending, flexing, twisting, kneeling, reaching overhead above shoulders. Pushing, pulling, lifting and carrying a minimum of 35 pounds. Assist patients with repositioning, transfers, and/or transport. Move quickly to respond to emergencies.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_mobility_initials && student.std_mobility_initials !== '_____' ? student.std_mobility_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Motor Skills</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Coordination and dexterity</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Gross and fine motor skills are sufficient to perform patient care and procedures such as manipulate medical equipment and accessories, knobs, buttons, computers, and keyboards.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_motor_skills_initials && student.std_motor_skills_initials !== '_____' ? student.std_motor_skills_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Visual</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Use of sight</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Visual skills, depth perception, color identification necessary to perform assessments, including signs and symptoms; read body/facial expressions; read and interpret written words.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_visual_initials && student.std_visual_initials !== '_____' ? student.std_visual_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Hearing</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Use of auditory sense</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Ability to hear and interpret environmental noises, including verbal orders, equipment and fire alarms, cries for help, elements of physical assessment.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_hearing_initials && student.std_hearing_initials !== '_____' ? student.std_hearing_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Tactile</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Use of touch</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Ability to sense heat, cold, pain, pressure. Accurately elicit, interpret, and convey medical and other info using verbal, nonverbal, written, assisted (TTY) and/or electronic devices.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_tactile_initials && student.std_tactile_initials !== '_____' ? student.std_tactile_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Communication</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Use the English language effectively by means of speech, reading, and writing. Demonstrate sensitive and effective interactions with patients, families, and health care team.</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Effectively communicate with individuals and teams. Determine a deeper meaning or significance in what is being expressed. Connect with others to sense and stimulate reactions.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ borderBottom: '1px', minHeight: '20px', width: '100%' }}>{student.std_communication_initials && student.std_communication_initials !== '_____' ? student.std_communication_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Acquire Knowledge</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Ability to possess clinical inquiry, seek resources of knowledge, and become a life-long learner.</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Demonstrate curiosity while learning. Acquire, conceptualize and use evidence-based information. Develop solutions and responses beyond memorization.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_acquire_knowledge_initials && student.std_acquire_knowledge_initials !== '_____' ? student.std_acquire_knowledge_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Clinical Judgment</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Ability to critical think, solve problems and make decisions. Intellectual and conceptual abilities.</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Accomplish, direct, and interpret assessment of persons, families and/or communities. Develop, implement and evaluate plans of care or direct the development, implementation and evaluation of care.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_clinical_judgment_initials && student.std_clinical_judgment_initials !== '_____' ? student.std_clinical_judgment_initials : ''}</div>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Professional Attitude</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>Demonstrate concern for others, integrity, ethical conduct, interest, motivation, and accountability. Acquire interpersonal skills for professional circumstances interactions with diverse individuals, families, and health care teams. Emotional and mental stability.</td>
                <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                  Maintain effective, mature, and sensitive relationships with clients/patients, students, faculty, staff and other professionals under all circumstances. Function effectively under stress and adapt to changing environments. Operate in different cultural settings. Work productively, drive engagement, and demonstrate presence as a member of a team.
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={{ minHeight: '20px', width: '100%' }}>{student.std_professional_attitude_initials && student.std_professional_attitude_initials !== '_____' ? student.std_professional_attitude_initials : ''}</div>
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginBottom: '8px', marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ borderBottom: '1px solid #000', paddingBottom: '6px' }}>
                <strong style={{ fontSize: '12px' }}>Student's Printed Name</strong>
                </div>
                <div style={{ marginTop: '4px', minHeight: '20px',paddingTop:5 }}>
                  {student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ borderBottom: '1px solid #000', paddingBottom: '6px' }}>
                <strong style={{ fontSize: '12px' }}>Student's Signature</strong>
                </div>
                <div style={{ marginTop: '4px', minHeight: '20px',paddingTop:5 }}>
                  {student.student_ack_signature_data ? (
                    <img
                      src={student.student_ack_signature_data}
                      alt="Student Signature"
                      style={{
                        maxHeight: '40px',
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No signature available</span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ borderBottom: '1px solid #000', paddingBottom: '6px' }}>
                <strong style={{ fontSize: '12px' }}>Date</strong>
                </div>
                <div style={{ marginTop: '4px', minHeight: '20px',paddingTop:5 }}>
                  {student.acknowledgementDate || new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </Page>
        {/* Page 3 */}
        <Page>
          <Logo src={process.env.PUBLIC_URL + '/form_logo_2.png'} alt="CCLC Logo" style={{ marginBottom: '0' }} />

          {/* For Office Use Only Box */}
          <div style={{ border: '1px solid #000', padding: '8px', marginBottom: '16px', width: '200px', fontSize: '0.8rem' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>For Office Use Only</div>
            <div>Reviewed by: </div>
            <div>Date: </div>
            <div>Comment: </div>
          </div>

          <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '12px', marginBottom: '16px', marginTop: '30px' }}>
            MEDICAL EVALUATION/ PHYSICAL EXAMINATION FORM- PART 2 OF 2
          </div>

          {/* Student Information and Permission Section */}
          <div style={{ marginBottom: '16px', fontSize: '0.9rem' }}>

            <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
              Chicago Community Learning Center has my permission to release these test results to the clinical education agencies to which I am assigned.
            </div>
          </div>

          <div style={{ fontWeight: 700, marginTop: '16px', marginBottom: '8px', fontSize: '13px', textAlign: 'center' }}>PART II: To be completed by Healthcare Provider</div>
          
          {!medicalEvalData && !medicalEvalLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              margin: '20px 0',
              backgroundColor: '#ffeaa7',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              color: 'black',
              fontSize: '14px'
            }}>
              <strong>Ask the student to fill in the medical evaluation form</strong>
            </div>
          )}

          {/* Measurements and Vitals Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', display: 'inline-table' }} border="1">
            <thead>
              <tr style={{}}>
                <th style={{ width: '25%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Height</th>
                <th colSpan={2} style={{ width: '25%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Weight</th>
                <th style={{ width: '25%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>HR</th>
                <th style={{ width: '25%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>BP</th>
              </tr>
              <tr>
                <td style={{ padding: '4px', fontSize: '12px' }}>{student.bc_height_ft || ''} ft {student.bc_height_in || ''} in</td>
                <td colSpan={2} style={{ padding: '4px', fontSize: '12px' }}>{student.bc_weight || ''} lbs</td>
                <td style={{ padding: '4px', fontSize: '12px' }}>{medicalEvalData?.heart_rate || ''}</td>
                <td style={{ padding: '4px', fontSize: '12px' }}>{medicalEvalData?.systolic || ''} (Systolic) : {medicalEvalData?.diastolic || ''} (Diastolic)</td>
              </tr>
            </thead>
            <tbody>

            </tbody>

            <thead>
              <tr style={{}}>
                <th style={{ width: '30%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Hearing and Vision</th>
                <th colSpan={2} style={{ width: '20%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Right</th>
                <th style={{ width: '20%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Left</th>
                <th style={{ width: '30%', padding: '4px', fontSize: '12px', textAlign: 'left' }}>Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '4px', fontSize: '12px' }}>Is conversation hearing normal?</td>
                <td colSpan={2} style={{ padding: '4px' }}>
                  <input type="checkbox" checked={medicalEvalData?.hearing_right === 'Yes' || medicalEvalData?.hearing_right === 'yes'} readOnly style={{ width: '10px', height: '10px' }} /> Yes
                  <input type="checkbox" checked={medicalEvalData?.hearing_right === 'No' || medicalEvalData?.hearing_right === 'no'} readOnly style={{ width: '10px', height: '10px' }} /> No
                </td>
                <td style={{ padding: '4px', fontSize: '12px' }}>
                  <input type="checkbox" checked={medicalEvalData?.hearing_left === 'Yes' || medicalEvalData?.hearing_left === 'yes'} readOnly style={{ width: '10px', height: '10px' }} /> Yes
                  <input type="checkbox" checked={medicalEvalData?.hearing_left === 'No' || medicalEvalData?.hearing_left === 'no'} readOnly style={{ width: '10px', height: '10px' }} /> No
                </td>
                <td style={{ padding: '8px 4px' }} rowSpan="2">{medicalEvalData?.hearing_right_value || medicalEvalData?.hearing_left_value || ''}</td>
              </tr>
              <tr>
                <td style={{ padding: '4px', fontSize: '12px' }}>Visual Acuity</td>
                <td colSpan={2} style={{ padding: '4px', fontSize: '12px' }}>{medicalEvalData?.hearing_right_value || ''}/20</td>
                <td style={{ padding: '4px', fontSize: '12px' }}>{medicalEvalData?.hearing_left_value || ''}/20</td>
              </tr>
            </tbody>

            
            <thead>
              <tr>
                <td style={{ fontSize: '12px', textAlign: 'center' }}>Check the appropriate response: </td>
                <td style={{ fontSize: '12px', textAlign: 'center' }}>Normal</td>
                <td style={{ fontSize: '12px', textAlign: 'center' }}>Abnormal</td>
                <td style={{ fontSize: '12px', textAlign: 'center' }}>Not Examined</td>
                <td style={{ width: '15%', padding: '8px 4px', fontSize: '12px', textAlign: 'center' }}>Significant Findings/Comments</td>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: '4px' }}>Skin</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.skin === 'Normal' || medicalEvalData?.skin === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.skin === 'Abnormal' || medicalEvalData?.skin === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.skin === 'Not Examined' || medicalEvalData?.skin === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.skin_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>EENT & Mouth</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.eent === 'Normal' || medicalEvalData?.eent === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.eent === 'Abnormal' || medicalEvalData?.eent === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.eent === 'Not Examined' || medicalEvalData?.eent === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.eent_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Neurological</td><td style={{ padding: '8px 4px', textAlign: 'center' }}></td><td style={{ padding: '8px 4px', textAlign: 'center' }}></td><td style={{ padding: '8px 4px', textAlign: 'center' }}></td><td style={{ padding: '8px 4px' }}>{student.neurological_findings || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Respiratory</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.respiratory === 'Normal' || medicalEvalData?.respiratory === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.respiratory === 'Abnormal' || medicalEvalData?.respiratory === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.respiratory === 'Not Examined' || medicalEvalData?.respiratory === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.respiratory_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Cardiovascular</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.cardiovascular === 'Normal' || medicalEvalData?.cardiovascular === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.cardiovascular === 'Abnormal' || medicalEvalData?.cardiovascular === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.cardiovascular === 'Not Examined' || medicalEvalData?.cardiovascular === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.cardiovascular_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Gastrointestinal</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.gastrointestinal === 'Normal' || medicalEvalData?.gastrointestinal === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.gastrointestinal === 'Abnormal' || medicalEvalData?.gastrointestinal === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.gastrointestinal === 'Not Examined' || medicalEvalData?.gastrointestinal === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.gastrointestinal_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Genitourinary</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.genitourinary === 'Normal' || medicalEvalData?.genitourinary === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.genitourinary === 'Abnormal' || medicalEvalData?.genitourinary === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.genitourinary === 'Not Examined' || medicalEvalData?.genitourinary === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.genitourinary_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Musculoskeletal</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.musculoskeletal === 'Normal' || medicalEvalData?.musculoskeletal === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.musculoskeletal === 'Abnormal' || medicalEvalData?.musculoskeletal === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.musculoskeletal === 'Not Examined' || medicalEvalData?.musculoskeletal === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.musculoskeletal_comments || ''}</td></tr>
              <tr><td style={{ padding: '4px' }}>Spine</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.spine === 'Normal' || medicalEvalData?.spine === 'normal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.spine === 'Abnormal' || medicalEvalData?.spine === 'abnormal') ? '✓' : ''}</td><td style={{ padding: '8px 4px', textAlign: 'center' }}>{(medicalEvalData?.spine === 'Not Examined' || medicalEvalData?.spine === 'not examined') ? '✓' : ''}</td><td style={{ padding: '8px 4px' }}>{medicalEvalData?.spine_comments || ''}</td></tr>
            </tbody>
          </table>
 
          <div style={{ marginBottom: '16px', border: '1px solid #000', padding: '8px' }}>
            <div style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
              <strong>1.</strong> If health conditions/pregnancy are present, does pregnancy or other health conditions create a limitation for the student participating in physical activities in the clinical area?
              <span style={{ marginTop: '4px', marginLeft: '10px' }}>
                <input type="checkbox" checked={medicalEvalData?.limitation === 'No' || medicalEvalData?.limitation === 'no'} readOnly /> No
                <input type="checkbox" checked={medicalEvalData?.limitation === 'Yes' || medicalEvalData?.limitation === 'yes'} readOnly style={{ marginLeft: '20px' }} /> Yes
                {(medicalEvalData?.limitation === 'Yes' || medicalEvalData?.limitation === 'yes') && (
                  <span style={{ marginLeft: '10px' }}>- please specify limitations: <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px', marginLeft: '8px' }}>{medicalEvalData?.limitation_details || ''}</span></span>
                )}
              </span>
            </div>

            <div style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
              <strong>2.</strong> Does your examination reveal any infectious process?
              <span style={{ marginTop: '4px', marginLeft: '10px' }}>
                <input type="checkbox" checked={medicalEvalData?.infectious === 'No' || medicalEvalData?.infectious === 'no'} readOnly /> No
                <input type="checkbox" checked={medicalEvalData?.infectious === 'Yes' || medicalEvalData?.infectious === 'yes'} readOnly style={{ marginLeft: '20px' }} /> Yes
              </span>
            </div>

            <div style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
              <strong>3.</strong> List all current medication(s) (include Rx, OTC, herbal/vitamins)
              <div style={{ marginTop: '4px' }}>
                Please List: <span style={{ display: 'inline-block', minWidth: '300px', marginLeft: '8px' }}>{medicalEvalData?.medications || ''}</span>
              </div>
            </div>

            <div style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
              <strong>4.</strong> Existing Medical Conditions?
              <span style={{ marginTop: '4px' }}>
                <input type="checkbox" checked={medicalEvalData?.existing_conditions === 'No' || medicalEvalData?.existing_conditions === 'no'} readOnly /> No
                <input type="checkbox" checked={medicalEvalData?.existing_conditions === 'Yes' || medicalEvalData?.existing_conditions === 'yes'} readOnly style={{ marginLeft: '20px' }} /> Yes
                {(medicalEvalData?.existing_conditions === 'Yes' || medicalEvalData?.existing_conditions === 'yes') && (
                  <span style={{ marginLeft: '10px' }}>- please list: <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px', marginLeft: '8px' }}>{medicalEvalData?.conditions_list || ''}</span></span>
                )}
              </span>
            </div>
          </div>

          {/* Healthcare Provider Information */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px', display: 'inline-table' }} border="1">
            <thead>
              <tr>
                <td colSpan={2} style={{ textAlign: 'center', padding: '4px 0', marginTop: '16px', marginBottom: '8px' }}>Health Care Provider Information</td>

              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: '50%', padding: '8px 4px' }}>Print Name: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_name || ''}</span></td>
                <td style={{ width: '50%', padding: '8px 4px' }}>Signature: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_signature || ''}</span></td>
              </tr>
              <tr>
                <td style={{ width: '50%', padding: '8px 4px' }}>Address: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_address || ''}</span></td>
                <td style={{ width: '50%', padding: '8px 4px' }}>City: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_city || ''}</span></td>
              </tr>
              <tr>
                <td style={{ width: '50%', padding: '8px 4px' }}>State: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_state || ''}</span></td>
                <td style={{ width: '50%', padding: '8px 4px' }}>Zip Code: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_zip || ''}</span></td>
              </tr>
              <tr>
                <td style={{ width: '50%', padding: '8px 4px' }}>Telephone: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.provider_phone || ''}</span></td>
                <td style={{ width: '50%', padding: '8px 4px' }}>Date: <span style={{ display: 'inline-block', minWidth: '150px', marginLeft: '8px' }}>{student.examination_date || ''}</span></td>
              </tr>
            </tbody>
          </table>

        </Page>
        {/* Page 4 */}
        <Page>
          <div style={{ display: 'flex' }}>
            <Logo src={process.env.PUBLIC_URL + '/form_logo_2.png'} alt="CCLC Logo" style={{ marginBottom: '0' }} />
          </div>
          {/* Instructions */}
          <div style={{ fontSize: '12px', lineHeight: '1.4', marginBottom: '20px', marginTop: '16px', textAlign: 'center' }}>
            Must be completed and turned in as part of the CNA application package. The QuantiFERON Gold-TB test can be administered through a personal physician or the Cook County Health Department.
          </div>

          {/* Personal Information Section */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <div style={{ fontSize: '12px', textAlign: 'center',borderBottom: '1px solid #000',paddingBottom: '6px' }}>First Name</div>
              </div>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <div style={{ fontSize: '12px', textAlign: 'center',borderBottom: '1px solid #000',paddingBottom: '6px' }}>Last Name</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', textAlign: 'center',borderBottom: '1px solid #000',paddingBottom: '6px' }}>Date of Birth</div>
              </div>
            </div>

            {/* Fill with student data */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ flex: 1, marginRight: '16px', textAlign: 'center', fontSize: '12px' }}>
                {student.firstName || ''}
              </div>
              <div style={{ flex: 1, marginRight: '16px', textAlign: 'center', fontSize: '12px' }}>
                {student.lastName || ''}
              </div>
              <div style={{ flex: 1, textAlign: 'center', fontSize: '12px' }}>
                {student.bc_dob || ''}
              </div>
            </div>
          </div>

          {/* TB Test History Section */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ border: '1px solid #000', display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ padding: '4px 12px', marginRight: '12px', fontSize: '12px' }}>
                Have you ever had a positive TB test?
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                <input type="checkbox" checked={student.tb_test_positive === 'Yes'} readOnly style={{ marginRight: '4px' }} />
                <span style={{ fontSize: '12px' }}>Yes</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={student.tb_test_positive === 'No'} readOnly style={{ marginRight: '4px' }} />
                <span style={{ fontSize: '12px' }}>No</span>
              </div>
            </div>
          </div>

          {/* QuantiFERON-TB Test Details */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '12px', lineHeight: '1.4', marginBottom: '16px', textAlign: 'center' }}>
              QuantiFERON-TB Test results will only be kept on file and counted toward meeting this requirement one year from the date first given in QuantiFERON-TB test step 1 below:
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ marginBottom: '8px', display: 'flex', marginRight: '20px' }}>
                <p style={{ fontSize: '12px', marginRight: '8px' }}>QuantiFERON Gold:</p>
                <span style={{ borderBottom: '1px solid #000', flex: 1, height: '20px', marginLeft: '8px', minWidth: '120px' }}> {student.quanti_feron_gold || ''}</span>
              </div>
              <div style={{ marginBottom: '8px', display: 'flex' }}>
                <p style={{ fontSize: '12px', marginRight: '8px' }}>Date Performed:</p>
                <span style={{ borderBottom: '1px solid #000', flex: 1, height: '20px', marginLeft: '8px', minWidth: '120px' }}>{student.quanti_feron_date || ''}</span>
              </div>



            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <p style={{ fontSize: '12px', marginRight: '8px' }}>Results:</p>
                <span style={{ borderBottom: '1px solid #000', flex: 1, height: '20px', marginLeft: '8px', minWidth: '120px' }}> {student.quanti_feron_results || ''}</span>
              </div>

            </div>
          </div>

          {/* Additional Note */}
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '32px', textAlign: 'center' }}>
            **If you have a QuantiFERON-TB test within last 6 months you may submit those results**
          </div>

          {/* Health Care Provider Stamp Box */}
          <div style={{ border: '1px solid #000', width: '300px', height: '150px', margin: '0 auto', position: 'relative' }}>
            <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', fontSize: '12px', textAlign: 'center', width: 'max-content' }}>
              Health Care Provider Stamp Here
            </div>
          </div>
        </Page>

      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>
        Print Form
      </DownloadButton>
    </Container>
  );
};

export default MedicalEvaluationForm; 